import { useNavigate } from "react-router-dom";
import { useDocPrice, priceLabel } from "@/utils/docPrices";
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import {
  Check,
  X,
  Shield,
  FileText,
  Zap,
  Users,
  Download,
  Eye,
  Sparkles,
  Star,
  DollarSign
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MintSlipLogo from "../assests/mintslip-logo.png";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

const STATS = [
  { value: "50%", label: "Cheaper", tone: "mint" },
  { value: "4+", label: "Template Styles", tone: "black" },
  { value: "50", label: "State Tax Calculations", tone: "gray" },
  { value: "24/7", label: "Instant Access", tone: "black" },
];

// [full label, short mobile label, MintSlip value, ThePayStubs value] — the
// short label keeps every capability on ONE line in the mobile table.
const COMPARISON_ROWS = (P) => [
  { name: "Price per Paystub", short: "Price per Paystub", mintslip: P, competitor: "$19.99" },
  { name: "Professional Templates", short: "Templates", mintslip: "4+ Professional Styles", competitor: "Multiple Generic Styles" },
  { name: "Instant Download", short: "Instant Download", mintslip: true, competitor: true },
  { name: "Blazing Fast Live Preview", short: "Live Preview", mintslip: true, competitor: false },
  { name: "All 50 State Tax Calculations", short: "50 State Taxes", mintslip: true, competitor: true },
  { name: "W-2 Employee Support", short: "W-2 Support", mintslip: true, competitor: true },
  { name: "1099 Contractor Support", short: "1099 Support", mintslip: true, competitor: true },
  { name: "YTD Calculations", short: "YTD Calculations", mintslip: true, competitor: true },
  { name: "Overtime Calculations", short: "Overtime", mintslip: true, competitor: true },
  { name: "Custom Deductions", short: "Custom Deductions", mintslip: true, competitor: true },
  { name: "Employer Contributions", short: "Contributions", mintslip: true, competitor: false },
  { name: "Mobile Friendly", short: "Mobile Friendly", mintslip: true, competitor: true },
  { name: "No Account Required", short: "No Account Required", mintslip: true, competitor: true },
  { name: "Canadian Paystubs", short: "Canadian Paystubs", mintslip: true, competitor: false },
  { name: "Mobile App", short: "Mobile App", mintslip: true, competitor: false },
];

const QUALITY_FEATURES = [
  { icon: FileText, text: "Multiple professional templates (Gusto, Workday, OnPay, ADP styles)" },
  { icon: Zap, text: "Instant PDF generation and download" },
  { icon: Shield, text: "Accurate tax calculations for all 50 states" },
  { icon: Eye, text: "Live preview as you type" },
  { icon: Users, text: "Support for both W-2 employees and 1099 contractors" },
  { icon: Download, text: "Unlimited revisions before purchase" },
];

const SWITCH_REASONS = [
  {
    icon: DollarSign,
    title: "Better Value",
    description: "Save over 50% on every paystub without sacrificing quality. More money in your pocket.",
    tone: "gray",
  },
  {
    icon: Zap,
    title: "Faster & Easier",
    description: "Our intuitive interface lets you create professional paystubs in under 5 minutes. No learning curve.",
    tone: "mint",
  },
  {
    icon: Shield,
    title: "More Features",
    description: "From contractor support to custom deductions, MintSlip offers features ThePayStubs doesn't have.",
    tone: "black",
  },
];

const FAQS = (P) => [
  {
    q: "Is MintSlip really cheaper than ThePayStubs?",
    a: `Yes! MintSlip charges ${P} per paystub while ThePayStubs charges $19.99. That's a real saving on every document you create.`
  },
  {
    q: "Are MintSlip paystubs as professional as ThePayStubs?",
    a: "Absolutely. MintSlip offers multiple professional templates including styles that match popular payroll providers like Gusto, Workday, OnPay, and ADP. Our paystubs are accepted by landlords, banks, and employers nationwide."
  },
  {
    q: "Does MintSlip calculate taxes accurately?",
    a: "Yes, MintSlip calculates federal taxes, Social Security, Medicare, and state taxes for all 50 US states. Our tax tables are regularly updated to ensure accuracy."
  },
  {
    q: "Can I create paystubs for contractors on MintSlip?",
    a: "Yes, MintSlip supports both W-2 employees and 1099 contractors."
  },
  {
    q: "How fast can I get my paystub?",
    a: "Instantly! Once you complete your information and payment, your PDF is available for immediate download. No waiting, no email delays."
  },
  {
    q: "Do I need to create an account?",
    a: "No account required. MintSlip lets you create and download paystubs without signing up."
  }
];

export default function MintSlipVsThePayStubs() {
  const navigate = useNavigate();
  // our pay stub price as the admin set it (Preferences > Document prices)
  const P = priceLabel(useDocPrice("paystub", 9.99));
  const [heroRef, heroInView] = useInView();
  const [priceRef, priceInView] = useInView();
  const [qualityRef, qualityInView] = useInView();
  const [tableRef, tableInView] = useInView();
  const [switchRef, switchInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Home page's compare-table cell treatments — green shine pill for yes,
  // muted X / small text for the competitor column.
  const renderMintCell = (v) =>
    v === true ? (
      <span
        className="cta-shine inline-flex items-center justify-center min-w-[56px] md:min-w-[96px] h-8 md:h-9 px-3 md:px-5 rounded-full text-white"
        style={{ background: "linear-gradient(180deg, #34d399, #10b981)", boxShadow: "0 8px 20px rgba(16, 185, 129, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.4)" }}
        aria-label="Yes"
      >
        <Check className="w-4 h-4 md:w-5 md:h-5" strokeWidth={3} />
      </span>
    ) : (
      <span className="text-[12.5px] md:text-sm font-semibold text-emerald-600">{v}</span>
    );

  const renderCompetitorCell = (v) =>
    v === true ? (
      <Check className="w-4 h-4 md:w-5 md:h-5 text-slate-400 inline" strokeWidth={2.5} aria-label="Yes" />
    ) : v === false ? (
      <X className="w-4 h-4 md:w-5 md:h-5 text-slate-300 inline" strokeWidth={2.5} aria-label="No" />
    ) : (
      <span className="text-[12.5px] md:text-sm text-slate-500">{v}</span>
    );

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>MintSlip vs ThePayStubs (2025) | Honest Comparison & Review</title>
        <meta name="description" content={`Comparing MintSlip vs ThePayStubs for paystub generation. See why MintSlip offers better value at ${P} vs $19.99, more templates, accurate tax calculations, and instant downloads.`} />
        <meta name="keywords" content="MintSlip vs ThePayStubs, paystub generator comparison, best paystub maker, ThePayStubs alternative, cheap paystub generator, paystub generator review" />
        <link rel="canonical" href="https://mintslip.com/mintslip-vs-thepaystubs" />

        {/* Open Graph */}
        <meta property="og:title" content="MintSlip vs ThePayStubs (2025) | Honest Comparison" />
        <meta property="og:description" content="See why thousands choose MintSlip over ThePayStubs. Better price, more features, instant downloads." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://mintslip.com/mintslip-vs-thepaystubs" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MintSlip vs ThePayStubs Comparison" />
        <meta name="twitter:description" content="Honest comparison of the two paystub generators. MintSlip wins on price, features, and quality." />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "MintSlip vs ThePayStubs: Complete Comparison Guide 2025",
            "description": "Detailed comparison between MintSlip and ThePayStubs paystub generators",
            "author": {
              "@type": "Organization",
              "name": "MintSlip"
            },
            "datePublished": "2025-01-01",
            "dateModified": "2025-01-01"
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">2025 Comparison Guide</span>
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            <span className="font-black">MintSlip</span> vs ThePayStubs
          </h1>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Looking for the best paystub generator? We compare MintSlip and ThePayStubs head-to-head
            on price, features, quality, and user experience. <strong>Save 50% with MintSlip.</strong>
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full shadow-sm">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <span className="text-slate-700 font-medium">{P} vs $19.99</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full shadow-sm">
              <Zap className="w-5 h-5 text-emerald-600" />
              <span className="text-slate-700 font-medium">Instant Download</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full shadow-sm">
              <Shield className="w-5 h-5 text-emerald-600" />
              <span className="text-slate-700 font-medium">More Templates</span>
            </div>
          </div>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Try MintSlip Today</span>
            </button>
          </div>
          <div className="flex justify-center mb-14">
            <button
              type="button"
              onClick={() => document.getElementById("comparison-table").scrollIntoView({ behavior: "smooth" })}
              className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
            >
              See Full Comparison
            </button>
          </div>

          {/* Quick stats bento */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {STATS.map((s) => {
              const t = toneCard[s.tone];
              return (
                <div key={s.label} className={`rounded-[28px] ${t.bg} px-4 py-10 md:px-8 md:py-14 flex flex-col items-center justify-center text-center`}>
                  <span className={`font-display font-black text-4xl sm:text-5xl md:text-6xl ${t.big}`}>{s.value}</span>
                  <span className={`mt-3 text-sm md:text-lg ${t.small}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price comparison */}
      <section ref={priceRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <div className={`transition-all duration-700 ${priceInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Price Comparison</span>
            </div>
          </div>

          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6">
            Save <span className="font-black">50%</span> with MintSlip
          </h2>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-10">
            ThePayStubs charges <strong className="text-red-500">$19.99</strong> per paystub while MintSlip offers
            the same professional quality at just <strong className="text-emerald-600">{P}</strong>.
            That's over 50% savings on every document you create.
          </p>
        </div>

        <div className={`transition-all duration-700 delay-150 ${priceInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="rounded-[28px] bg-slate-100 p-6 md:p-10 max-w-xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-600">ThePayStubs</span>
                <span className="text-xl font-bold text-slate-400 line-through">$19.99</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-900 font-medium">MintSlip</span>
                <span className="text-2xl font-bold text-emerald-600">{P}</span>
              </div>
              <div className="border-t border-slate-200 mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-700 font-medium">Your Savings</span>
                  <span className="text-xl font-bold text-emerald-600">$10.00 (50%)</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">
            * Prices as of January 2025. MintSlip pricing includes instant download.
          </p>
        </div>
      </section>

      {/* Quality & features */}
      <section ref={qualityRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <div className={`transition-all duration-700 ${qualityInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
              <Star className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Quality & Features</span>
            </div>
          </div>

          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6">
            More features, <span className="font-black">better quality</span>
          </h2>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12">
            MintSlip doesn't just save you money. It offers more features and better quality than ThePayStubs.
            Here's what you get with MintSlip:
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {QUALITY_FEATURES.map((item, i) => (
            <div
              key={item.text}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-5 md:p-6 flex items-start gap-4 transition-all duration-700 ${qualityInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 100}ms` }}
            >
              <span className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-white" />
              </span>
              <span className="text-slate-700 leading-relaxed">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Detailed comparison table — same treatment as the home page's compare table */}
      <section id="comparison-table" ref={tableRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24 scroll-mt-24">
        <div className={`transition-all duration-700 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
              <FileText className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Feature-by-Feature</span>
            </div>
          </div>

          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6">
            Complete <span className="font-black">comparison table</span>
          </h2>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12">
            See exactly how MintSlip and ThePayStubs compare across all important features.
          </p>
        </div>

        <div className={`transition-all duration-700 delay-200 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="rounded-[20px] border border-slate-200 overflow-hidden bg-white max-w-4xl mx-auto">
            <table className="w-full border-collapse text-[15px]">
              <thead>
                <tr>
                  <th className="px-2 md:px-6 py-3.5 md:py-4 text-left bg-slate-50 border-b border-slate-200 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Feature</th>
                  <th className="px-1 md:px-6 py-3.5 md:py-4 text-center bg-slate-50 border-b border-slate-200 w-[86px] md:w-[150px]">
                    <img src={MintSlipLogo} alt="MintSlip" className="h-[18px] md:h-6 w-auto inline-block align-middle" />
                  </th>
                  <th className="px-1 md:px-6 py-3.5 md:py-4 text-center bg-slate-50 border-b border-slate-200 w-[80px] md:w-[150px] text-[9px] md:text-xs font-semibold uppercase tracking-[0.08em] md:tracking-[0.15em] text-slate-400">ThePayStubs</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS(P).map((f, i) => {
                  const bb = i === COMPARISON_ROWS(P).length - 1 ? "" : " border-b border-slate-200";
                  return (
                    <tr key={f.name}>
                      <td className={`px-2 md:px-6 py-5 md:py-4 text-slate-800 text-[13px] md:text-[15px] whitespace-nowrap${bb}`}>
                        <span className="hidden md:inline">{f.name}</span>
                        <span className="md:hidden">{f.short}</span>
                      </td>
                      <td className={`px-1 md:px-6 py-5 md:py-4 text-center${bb}`}>{renderMintCell(f.mintslip)}</td>
                      <td className={`px-1 md:px-6 py-5 md:py-4 text-center${bb}`}>{renderCompetitorCell(f.competitor)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why people switch */}
      <section ref={switchRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${switchInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Why people switch to <span className="font-black">MintSlip</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${switchInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Thousands of users have made the switch from ThePayStubs to MintSlip. Here's what they love:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SWITCH_REASONS.map((item, i) => {
            const t = toneCard[item.tone];
            return (
              <div
                key={item.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${switchInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <item.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-2xl md:text-3xl font-medium mb-3 ${t.big}`}>{item.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <div className="max-w-3xl mx-auto space-y-5">
          {FAQS(P).map((faq, i) => (
            <div
              key={faq.q}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-6 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${100 + i * 80}ms` }}
            >
              <h3 className="font-display text-lg font-bold text-slate-900 mb-3">{faq.q}</h3>
              <p className="text-slate-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to <span className="font-black">save 50%</span> on your next paystub?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Join thousands who've switched from ThePayStubs to MintSlip. Better quality, more features, lower price.
          </p>
          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create Your Paystub Now</span>
            </button>
          </div>
          <p className="text-slate-500 text-sm">
            No account required • Instant download • 100% satisfaction guaranteed
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
